import { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Polyline, useMap } from 'react-leaflet';
import type { LatLngExpression, LatLngBounds } from 'leaflet';
import L from 'leaflet';
import './RouteMap.css';

interface RouteMapProps {
  gpxFile: string;
  mini?: boolean;
}

interface GpxData {
  coordinates: LatLngExpression[];
  bounds: LatLngBounds | null;
}

function FitBounds({ bounds }: { bounds: LatLngBounds | null }) {
  const map = useMap();

  useEffect(() => {
    // Fix for map tiles not loading - invalidate size after render
    const timer1 = setTimeout(() => {
      map.invalidateSize();
    }, 100);

    const timer2 = setTimeout(() => {
      map.invalidateSize();
      if (bounds) {
        map.fitBounds(bounds, { padding: [20, 20] });
      }
    }, 300);

    if (bounds) {
      map.fitBounds(bounds, { padding: [20, 20] });
    }

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, [bounds, map]);

  return null;
}

function parseGpx(gpxText: string): GpxData {
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(gpxText, 'text/xml');

  // Try with namespace first (GPX 1.1), then without (GPX 1.0)
  const gpxNs = 'http://www.topografix.com/GPX/1/1';
  let trackPoints = xmlDoc.getElementsByTagNameNS(gpxNs, 'trkpt');

  // Fallback to non-namespaced if no points found
  if (trackPoints.length === 0) {
    trackPoints = xmlDoc.getElementsByTagName('trkpt');
  }

  const coordinates: LatLngExpression[] = [];

  for (let i = 0; i < trackPoints.length; i++) {
    const point = trackPoints[i];
    const lat = Number.parseFloat(point.getAttribute('lat') || '0');
    const lon = Number.parseFloat(point.getAttribute('lon') || '0');
    if (lat !== 0 && lon !== 0) {
      coordinates.push([lat, lon]);
    }
  }

  let bounds: LatLngBounds | null = null;
  if (coordinates.length > 0) {
    bounds = L.latLngBounds(coordinates as L.LatLngExpression[]);
  }

  return { coordinates, bounds };
}

export function RouteMap({ gpxFile, mini = false }: RouteMapProps): JSX.Element {
  const [gpxData, setGpxData] = useState<GpxData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const containerClass = `route-map-container${mini ? ' route-map-mini' : ''}`;

  useEffect(() => {
    async function loadGpx() {
      try {
        setLoading(true);
        setError(null);
        const response = await fetch(gpxFile);
        if (!response.ok) {
          throw new Error('Failed to load GPX file');
        }
        const gpxText = await response.text();
        const data = parseGpx(gpxText);
        if (data.coordinates.length === 0) {
          throw new Error('No route data found in GPX file');
        }
        setGpxData(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load route');
      } finally {
        setLoading(false);
      }
    }

    loadGpx();
  }, [gpxFile]);

  if (loading) {
    return (
      <div className={`${containerClass} route-map-loading`}>
        <span>Loading map...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`${containerClass} route-map-error`}>
        <span>⚠️ {error}</span>
      </div>
    );
  }

  const defaultCenter: LatLngExpression =
    gpxData && gpxData.coordinates.length > 0 ? gpxData.coordinates[0] : [51.505, -0.09];

  return (
    <div className={containerClass}>
      <MapContainer
        center={defaultCenter}
        zoom={13}
        scrollWheelZoom={!mini}
        dragging={!mini}
        touchZoom={!mini}
        doubleClickZoom={!mini}
        zoomControl={!mini}
        boxZoom={!mini}
        keyboard={!mini}
        className="route-map"
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          subdomains={['a', 'b', 'c']}
        />
        {gpxData && (
          <>
            <Polyline positions={gpxData.coordinates} color="#e63946" weight={4} opacity={0.8} />
            <FitBounds bounds={gpxData.bounds} />
          </>
        )}
      </MapContainer>
    </div>
  );
}
